import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as customInstrumentsApi from "@/lib/api/custom-instruments";

export function useCustomInstruments() {
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ["customInstruments"],
    queryFn: () => customInstrumentsApi.list(),
  });
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["customInstruments"] });
  return { instruments: data ?? [], error, isLoading, mutate };
}

export function useCustomInstrument(ticker: string | null) {
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ["customInstruments", ticker],
    queryFn: () => customInstrumentsApi.getByTicker(ticker!),
    enabled: !!ticker,
  });
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["customInstruments", ticker] });
  return { instrument: data ?? null, error, isLoading, mutate };
}

export function useCustomInstrumentStatistics(ticker: string | null, currency: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["customInstruments", ticker, "statistics"],
    queryFn: () => customInstrumentsApi.getStatistics(ticker!, currency!),
    enabled: !!ticker && !!currency,
  });
  return { stats: data ?? null, error, isLoading };
}

export function useCreateCustomInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: customInstrumentsApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customInstruments"] }),
  });
}

export function useUpdateCustomInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticker,
      data,
    }: {
      ticker: string;
      data: Parameters<typeof customInstrumentsApi.update>[1];
    }) => customInstrumentsApi.update(ticker, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customInstruments"] }),
  });
}

export function useDeleteCustomInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: customInstrumentsApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customInstruments"] }),
  });
}
